import { useEffect, useMemo, Fragment } from "react";
import type { Quote, Material, MaterialCategory, QuoteTax } from "@/types";
import type { BackendCompanySettings, LivePricedLine } from "@/services/api";
import { formatDate, formatRupees, numberToWords } from "@/lib/format";
import { colors, semanticColors, radius, spacing, heights, layout, typography, shadows, overlayMaxWidth } from "@/styles/tokens";

// ============================================================================
// CRITICAL — this document is sent to customers. It must NEVER show internal
// commercial data: OEM list/buy price or any cost, discount %, freight/duty/
// landed cost, margin/margin %/any buffer, FX rate or buffer, origin/
// sourcing split/factory, stock levels/warehouse names, or brand-lock
// warnings/flags. Only unit sell price, quantity, and line total may appear.
//
// toDocLines() below is the ONLY place that reads the full Material object.
// Everything else in this file works exclusively with DocLine, which has
// just the 8 customer-safe fields. That's deliberate: it means a future
// change can't accidentally render a forbidden field just by reaching for
// `material.xyz` somewhere further down — DocLine structurally doesn't have
// it. If a new field is ever needed on the document, add it to DocLine on
// purpose and check it against the forbidden list above first.
// ============================================================================

interface DocLine {
  key: string;
  partNo: string;
  description: string;
  spec: string;
  qty: number;
  uom: string;
  // ENGINE VALUES. unit_sp / extended_sp off the backend result, read as-is
  // and never rederived. NOT Material.sellPrice/lineTotal: those are
  // services/pricing.ts placeholders, and printing them put a customer
  // document ~22% below the validated price.
  unitPrice: number;
  amount: number;
  category: MaterialCategory;
  // Descriptive, printed as-is.
  remark: string;
  leadTime: string;
  // "restricted" prints a zero and its reason and is left OUT of the total.
  // There is no error state here: a line the engine could not price never
  // becomes a DocLine at all -- toDocLines refuses the whole document
  // instead.
  restricted: boolean;
  restrictedReason: string;
}

/** THREE STATES, AND ONLY ONE OF THEM STOPS THE DOCUMENT.
 *
 * A zero on a quote line means one of two completely different things, and
 * until now this code could not tell them apart:
 *
 *   PRICED      the ordinary case. The engine's number is printed and
 *               counted.
 *   RESTRICTED  deliberately not quoted -- the customer asked, and the
 *               answer is "we cannot quote this, here is why". Prints zero
 *               WITH its reason, is excluded from the total, and the
 *               document generates. Real Anixify quotes carry these.
 *   UNPRICEABLE the engine has no answer for this line. Nothing prints: the
 *               whole document is refused.
 *
 * The first two are marked on the line by a person. The third is DETECTED
 * here, and detected in two ways, because a broken line can look fine:
 *
 *   - no engine result at all (never priced, or invalidated as stale), and
 *   - an engine result of zeros, which is what a line saved against no
 *     pricing config stores. `typeof 0 === "number"`, so the old check
 *     accepted it and printed Rs 0.00 to a customer under the engine's
 *     authority. gp_pct_snapshot is the tell: a real config always writes a
 *     GP into it, and a zeroed line has 0. It is the same signal the reopen
 *     guard uses -- this brings it to the document path, where it never ran.
 *
 * A restricted line is NOT subjected to that second test. It may legitimately
 * carry zeros, and its zero is intentional, which is precisely the
 * distinction the price_status field exists to record.
 */
function toDocLines(
  materials: Material[],
  livePricing: Map<number, LivePricedLine> | undefined,
): DocLine[] | null {
  const out: DocLine[] = [];
  for (const m of materials) {
    const restricted = m.priceStatus === "restricted";
    const priced = livePricing?.get(m.lineNo);

    if (!restricted) {
      if (!priced
        || typeof priced.unit_sp !== "number"
        || typeof priced.extended_sp !== "number"
        || !(priced.gp_pct_snapshot > 0)) {
        return null;
      }
    }

    out.push({
      key: `${m.lineNo}-${m.partNo}`,
      partNo: m.partNo,
      description: m.description,
      spec: m.spec,
      qty: m.qty,
      uom: m.uom,
      // A restricted line prints zero whatever the engine said about it. The
      // engine still priced it -- the status decides what the document does
      // with the result, not what the result is.
      unitPrice: restricted ? 0 : priced!.unit_sp,
      amount: restricted ? 0 : priced!.extended_sp,
      category: m.category,
      remark: m.remark ?? "",
      leadTime: m.leadTime ?? "",
      restricted,
      restrictedReason: m.priceStatusReason ?? "",
    });
  }
  return out;
}

const CATEGORY_ORDER: MaterialCategory[] = ["copper", "fiber", "accessory"];
const CATEGORY_LABEL: Record<MaterialCategory, string> = {
  copper: "Copper", fiber: "Fiber", accessory: "Accessories",
};

const GST_RATE = 0.18;

/** Two decimals, applied to the total rather than to each line. */
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

const TERMS = [
  "Prices in INR, ex-warehouse Bangalore unless stated",
  "Delivery: 4 to 6 weeks from confirmed purchase order",
  "Payment: 30 days from invoice date",
  "Prices subject to revision if the order is placed after the validity date",
  "Taxes as applicable at the time of dispatch",
];


const SECTION_LABEL: React.CSSProperties = {
  fontSize: typography.fontSize.xs, color: semanticColors.textMuted,
  textTransform: "uppercase", letterSpacing: typography.letterSpacing.wide,
  fontWeight: typography.fontWeight.medium,
};
const CELL: React.CSSProperties = {
  padding: `${spacing[8]}px`, fontSize: typography.fontSize.sm, color: semanticColors.textPrimary,
  borderBottom: `1px solid ${semanticColors.border}`,
};

function TotalsRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between" style={{ padding: `${spacing[4]}px 0` }}>
      <span style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary }}>{label}</span>
      <span style={{ fontSize: typography.fontSize.sm, color: semanticColors.textPrimary, fontVariantNumeric: "tabular-nums" }}>{value}</span>
    </div>
  );
}

export function QuotePdfPreview({
  quote,
  materials,
  livePricing,
  quoteName,
  company,
  tax,
  taxPending,
  onClose,
}: {
  quote: Quote;
  materials: Material[];
  // The SAME map MaterialTable is handed. Without it this component cannot
  // see engine numbers at all, which is precisely why it printed placeholder
  // ones -- and why no test could pin its figures until this prop existed.
  livePricing?: Map<number, LivePricedLine>;
  // The reference the backend assigned, once there is one. The local quoteNo
  // is a client-side mock number and is not what the stored record is called.
  quoteName?: string;
  // THE ONE SOURCE OF COMPANY IDENTITY: Odoo's company_settings, fetched by
  // the caller. Nothing about the issuing company is hardcoded in this file
  // any more -- name, GSTIN, address, contacts and logo all come from here,
  // so the app and the document can never disagree about who sent the quote.
  company?: BackendCompanySettings;
  // gst-split (owner, 2026-09-24): THE TAX LINES, as the SERVER computed them
  // from stored sell values (CGST+SGST, IGST, or one flat line). Printed as
  // sent; nothing here derives a tax for a saved quote. Its `note` (why
  // there is no split) is for the app and is NEVER printed.
  tax?: QuoteTax;
  // The tax read for a saved quote is still in flight.
  taxPending?: boolean;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  // THE PRINT PATH. See the button below for why this is browser print and
  // not a PDF library.
  //
  // A class on <body> rather than a media query keyed on the overlay, because
  // the rules have to reach OUTSIDE this component: `.app-shell` is
  // h-screen/overflow-hidden and would otherwise clip the document to one
  // viewport, and printing it alongside the document would put the sidebar
  // and top bar on the customer's page. The rules live in index.css under
  // `body.printing-document` -- plain CSS, no token colours, so it does not
  // add to that file's manually-synced-duplicate problem.
  //
  // Cleared on unmount as well as set on mount: a print started and then
  // cancelled must not leave the app shell hidden.
  useEffect(() => {
    document.body.classList.add("printing-document");
    return () => document.body.classList.remove("printing-document");
  }, []);

  // null means at least one line has no engine price. Everything below is
  // written against an empty list in that case, and the document itself is
  // replaced by the blocked state -- see `unpriced` at the render.
  const lines = useMemo(() => toDocLines(materials, livePricing), [materials, livePricing]);
  const unpriced = lines === null;
  // Memoised so the identity is stable: a bare `lines ?? []` allocates a new
  // array every render, which makes the groups memo below recompute every
  // time and defeats the point of memoising it.
  const docLines = useMemo(() => lines ?? [], [lines]);

  const groups = useMemo(() => {
    let srNo = 0;
    return CATEGORY_ORDER
      .map((cat) => {
        const catLines = docLines.filter((l) => l.category === cat).map((l) => ({ ...l, srNo: ++srNo }));
        return {
          category: cat,
          label: CATEGORY_LABEL[cat],
          lines: catLines,
          // Restricted lines contribute nothing: their amount is zero by
          // construction above, so this needs no filter of its own.
          subtotal: catLines.reduce((s, l) => s + l.amount, 0),
        };
      })
      .filter((g) => g.lines.length > 0);
  }, [docLines]);

  const subtotal = useMemo(() => groups.reduce((s, g) => s + g.subtotal, 0), [groups]);
  // The customer, taken apart once. Ship-to is what a delivery document is
  // addressed to; the entity is the legal customer behind it.
  const termsLines = useMemo(() => {
    const stored = (company?.terms ?? "").split(String.fromCharCode(10))
      .map((t) => t.trim())
      .filter(Boolean);
    return stored.length > 0 ? stored : TERMS;
  }, [company?.terms]);

  // ⚠️ THE CUSTOMER AS ISSUED WINS (customer-snapshot-at-finalise, owner
  // 2026-09-23). A quote finalised with a snapshot prints the snapshot ONLY --
  // never the live customer, so a later edit of the customer cannot change a
  // document already issued. A draft, and a quote finalised before snapshots
  // existed, has none and prints the customer as it is now.
  const snap = quote.customerSnapshot;
  const entity = snap ? snap.billTo : (quote.customer?.entity ?? null);
  const shipTo = snap ? snap.shipTo : (quote.customer?.shipping ?? null);
  const contact = snap
    ? (snap.shipTo?.contact ?? snap.billTo?.contact ?? null)
    : (quote.customer?.contact ?? null);
  const shipToAddress = [
    shipTo?.street, shipTo?.street2, shipTo?.city, shipTo?.zip, shipTo?.state,
  ].filter(Boolean).join(", ");
  // BILL TO -- the customer COMPANY record itself (owner, 2026-09-23). The
  // same five address parts, and the same rule: an unset part prints nothing,
  // never a placeholder or "N/A".
  const billToAddress = [
    entity?.street, entity?.street2, entity?.city, entity?.zip, entity?.state,
  ].filter(Boolean).join(", ");
  // Which party blocks print. Bill to whenever there is a customer company;
  // the ship-to "To" block whenever there is a ship-to -- both when both. A
  // legacy quote with neither keeps the old name-only "To" block, so nothing
  // it printed before disappears.
  const showBillTo = !!entity?.name;
  const showShipTo = !!shipTo || !showBillTo;
  const partyColumns = (showBillTo ? 1 : 0) + (showShipTo ? 1 : 0) + 1;

  // THE TAXABLE BASE IS PRICED LINES ONLY. A restricted line was never
  // quoted, so taxing it would be charging tax on nothing. Their amounts are
  // already zero, which makes the exclusion arithmetic rather than a filter,
  // but it is worth naming: this is the number GST is charged on.
  const taxableBase = subtotal;
  // gst-split: a SAVED quote prints the server's tax lines -- but only when
  // they were computed on the very base this document shows. A server base
  // that differs means the screen holds edits the server has not seen; the
  // document is then BLOCKED (below) rather than print tax on another base.
  const serverTax = tax && Math.abs(tax.taxableBase - taxableBase) < 0.005 ? tax : undefined;
  // warehouse-master: whose GSTIN the document prints as ours. Never a
  // contact -- the warehouse's contacts never reach this file.
  const ourGstin = tax?.ourGstin || company?.gstin || "";
  const ourStateName = tax?.ourGstin ? (tax.ourStateName ?? "") : "";
  const taxStale = !!tax && !serverTax;
  // No server record at all (a shipped example quote, a quote never saved):
  // the one flat line this document has always printed, rounded on the total.
  const flatGst = round2(taxableBase * GST_RATE);
  const taxLines = serverTax
    ? serverTax.lines.map((t) => ({
        label: serverTax.rates.length > 1 ? `${t.label} on ${formatRupees(t.base)}` : t.label,
        amount: t.amount,
      }))
    : [{ label: "GST @ 18%", amount: flatGst }];
  const grandTotal = serverTax ? serverTax.grandTotal : round2(taxableBase + flatGst);
  // VALIDITY (quote-validity, owner 2026-09-23): printed only on a quote
  // that is NOT a draft and carries a stored valid_until. Empty prints
  // nothing -- no invented default, no placeholder.
  const showValidity = quote.status !== "Draft" && !!quote.validUntil;

  return (
    <div
      data-testid="pdf-preview-backdrop"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, backgroundColor: semanticColors.overlay, zIndex: 1000,
        overflowY: "auto", display: "flex", flexDirection: "column", alignItems: "center",
      }}
    >
      {/* Toolbar */}
      <div
        data-testid="pdf-preview-toolbar"
        onClick={(e) => e.stopPropagation()}
        className="flex items-center justify-between"
        style={{
          position: "sticky", top: 0, zIndex: 1, width: "100%",
          height: heights.filterBar, padding: `0 ${spacing[24]}px`,
          backgroundColor: semanticColors.surface, borderBottom: `1px solid ${semanticColors.border}`,
        }}
      >
        <span data-testid="pdf-preview-page-counter" style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary }}>
          Page 1 of 1
        </span>
        <div className="flex items-center" style={{ gap: spacing[8] }}>
          <button
            data-testid="pdf-preview-close"
            onClick={onClose}
            style={{
              height: heights.control, padding: `0 ${spacing[14]}px`, backgroundColor: semanticColors.surface,
              color: semanticColors.textSecondary, border: `1px solid ${semanticColors.border}`,
              borderRadius: radius.base, fontSize: typography.fontSize.base, cursor: "pointer",
            }}
          >
            Close
          </button>
          {/* ⚠️ THIS IS BROWSER PRINT, AND THE LABEL SAYS SO.
              It used to read "Download PDF" with no onClick and a title
              admitting no PDF library was wired up -- a primary button that
              did nothing on a screen whose whole purpose is producing a
              document to send.

              window.print() is not a stopgap here, it is the right answer.
              Every browser's print dialog offers "Save as PDF", so the output
              is a real PDF; it renders the document as TEXT rather than
              rasterising it, which a canvas-to-PDF library would not; it adds
              no dependency to a CRA bundle; and it prints what is on screen,
              so the customer-safe DocLine fence at the top of this file still
              decides what can appear. A library would be a second rendering
              path over the same data, which is exactly the kind of duplicate
              this project keeps paying to remove.

              The label is "Print / Save as PDF" because that is what happens:
              a dialog opens. "Download PDF" would promise a file appearing in
              the downloads folder, which is not what this does. */}
          <button
            data-testid="pdf-preview-download"
            onClick={() => window.print()}
            title="Opens your browser's print dialog. Choose “Save as PDF” to get a file."
            style={{
              height: heights.control, padding: `0 ${spacing[14]}px`, backgroundColor: colors.indigo600,
              color: semanticColors.onAccent, border: "none", borderRadius: radius.base,
              fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium, cursor: "pointer",
            }}
          >
            Print / Save as PDF
          </button>
        </div>
      </div>

      {/* Document page */}
      <div
        data-testid="pdf-preview-page"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: layout.pdfPageWidth,
          // ⚠️ SCREEN ONLY. The print rules in index.css set `width: auto`
          // under @media print, so this cap cannot narrow the printed page --
          // A4 is decided by @page, not by the viewport somebody previewed on.
          // Inert on screen at any width >= 810px -- see overlayMaxWidth.
          maxWidth: overlayMaxWidth,
          minHeight: layout.pdfPageMinHeight,
          backgroundColor: semanticColors.surface,
          border: `1px solid ${semanticColors.border}`,
          boxShadow: shadows.modal,
          margin: `${spacing[24]}px 0 ${spacing[40]}px`,
          padding: spacing[40], boxSizing: "border-box", flexShrink: 0,
        }}
      >
        {!unpriced && taxStale ? (
          // gst-split: BLOCKED, like an unpriced quote, never degraded. The
          // server's tax was computed on a different base than this document
          // shows, so printing it would put the wrong tax on a customer page.
          <div
            data-testid="pdf-preview-tax-stale"
            style={{
              minHeight: layout.pdfPageMinHeight - spacing[40] * 2,
              display: "flex", flexDirection: "column", gap: spacing[8],
              alignItems: "center", justifyContent: "center", textAlign: "center",
            }}
          >
            <div style={{ fontSize: typography.fontSize.lg, color: semanticColors.textPrimary }}>
              Save the quote before generating a document
            </div>
            <div style={{
              fontSize: typography.fontSize.base, color: semanticColors.textSecondary,
              maxWidth: 380, lineHeight: 1.5,
            }}>
              The tax is worked out from the saved quote, and the screen has changes it
              has not saved yet. Save, then generate the document again.
            </div>
          </div>
        ) : unpriced ? (
          // BLOCKED, not degraded. This document goes to a customer, so the
          // one thing it must never do is print a number the engine did not
          // produce. Falling back to the Material row is exactly how it came
          // to quote ~22% under the validated price.
          <div
            data-testid="pdf-preview-unpriced"
            style={{
              minHeight: layout.pdfPageMinHeight - spacing[40] * 2,
              display: "flex", flexDirection: "column", gap: spacing[8],
              alignItems: "center", justifyContent: "center", textAlign: "center",
            }}
          >
            <div style={{ fontSize: typography.fontSize.lg, color: semanticColors.textPrimary }}>
              Price the quote before generating a document
            </div>
            <div style={{
              fontSize: typography.fontSize.base, color: semanticColors.textSecondary,
              maxWidth: 380, lineHeight: 1.5,
            }}>
              Every line needs a price from the pricing engine. Choose
              &ldquo;Update prices&rdquo; on the quote, then generate the document.
            </div>
          </div>
        ) : docLines.length === 0 ? (
          <div
            data-testid="pdf-preview-empty"
            style={{
              minHeight: layout.pdfPageMinHeight - spacing[40] * 2,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: typography.fontSize.base, color: semanticColors.textPlaceholder,
            }}
          >
            Add materials before generating a quote document
          </div>
        ) : (
          <>
            {/* 1. Header */}
            <div className="flex items-start justify-between" style={{ marginBottom: spacing[24] }}>
              <div className="flex items-center" style={{ gap: spacing[12] }}>
                {/* The uploaded logo, from company settings. No bundled asset:
                    a document must show the company that actually sent it. */}
                {company?.logo_data_url ? (
                  <img
                    data-testid="pdf-company-logo"
                    src={company.logo_data_url}
                    alt={company.company_name || "Company logo"}
                    style={{ height: heights.pdfLogo, width: "auto", display: "block" }}
                  />
                ) : (
                  <div
                    data-testid="pdf-company-name-fallback"
                    style={{
                      fontSize: typography.fontSize.xl,
                      fontWeight: typography.fontWeight.semibold,
                      color: semanticColors.textPrimary,
                    }}
                  >
                    {company?.company_name || ""}
                  </div>
                )}
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{
                  fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.semibold, color: semanticColors.textPrimary,
                  letterSpacing: typography.letterSpacing.wide, textTransform: "uppercase",
                }}>
                  Quotation
                </div>
                <div data-testid="pdf-quote-no" style={{ fontSize: typography.fontSize.base, color: semanticColors.textSecondary, marginTop: spacing[4], fontVariantNumeric: "tabular-nums" }}>
                  {/* The reference the sequence assigned, once there is one.
                      quoteNo is a client-side mock number. */}
                  {quoteName ?? quote.quoteNo}
                </div>
                <div style={{ fontSize: typography.fontSize.sm, color: semanticColors.textMuted, marginTop: spacing[2], fontVariantNumeric: "tabular-nums" }}>
                  {formatDate(quote.createdAt)}
                </div>
              </div>
            </div>
            <div style={{ borderBottom: `1px solid ${semanticColors.border}`, marginBottom: spacing[24] }} />

            {/* 2. Parties */}
            <div style={{ display: "grid", gridTemplateColumns: `repeat(${partyColumns}, 1fr)`, gap: spacing[32], marginBottom: spacing[24] }}>
              {showBillTo && (
                <div data-testid="pdf-parties-bill-to">
                  <div style={SECTION_LABEL}>Bill to</div>
                  {/* THE CUSTOMER COMPANY -- name, address and GSTIN from the
                      record a person edits in Customer details. It reads the
                      customer as it is NOW, so a finalised quote reprints
                      current details (owner-accepted; snapshot deferred).
                      Every field prints only if it is set. */}
                  <div data-testid="pdf-bill-to-name" style={{ fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium, color: semanticColors.textPrimary, marginTop: spacing[6] }}>
                    {entity?.name}
                  </div>
                  {billToAddress && (
                    <div data-testid="pdf-bill-to-address" style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary, marginTop: spacing[2] }}>
                      {billToAddress}
                    </div>
                  )}
                  {entity?.vat && (
                    <div data-testid="pdf-bill-to-gstin" style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary, marginTop: spacing[2], fontVariantNumeric: "tabular-nums" }}>
                      GSTIN: {entity.vat}
                    </div>
                  )}
                  {/* The contact prints here only when there is no ship-to
                      block to carry it -- never twice. */}
                  {!showShipTo && contact?.name && (
                    <div data-testid="pdf-customer-contact" style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary, marginTop: spacing[4] }}>
                      Attn: {contact.name}
                      {contact.email ? ` · ${contact.email}` : ""}
                    </div>
                  )}
                </div>
              )}
              {showShipTo && (
              <div data-testid="pdf-parties-to">
                <div style={SECTION_LABEL}>To</div>
                {/* THE REAL CUSTOMER, from the quote's stored graph. This used
                    to be a client-side "integrator" name with a hardcoded
                    street address underneath it -- the address was invented,
                    and the name was not a record of anything.

                    Every field is printed only if it exists. A document
                    missing a line is recoverable; one carrying a made-up
                    address is not. */}
                <div data-testid="pdf-customer-name" style={{ fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium, color: semanticColors.textPrimary, marginTop: spacing[6] }}>
                  {shipTo?.name || entity?.name || quote.endClient}
                </div>
                {shipToAddress && (
                  <div data-testid="pdf-customer-address" style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary, marginTop: spacing[2] }}>
                    {shipToAddress}
                  </div>
                )}
                {/* The SHIP-TO's own GSTIN, which is the whole reason a
                    ship-to is a separate record: a branch has its own, and
                    printing the entity's would be the wrong number on a tax
                    document. Printed, never used to compute the tax below --
                    the GST layer is still a flat rate. */}
                {shipTo?.vat && (
                  <div data-testid="pdf-customer-gstin" style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary, marginTop: spacing[2], fontVariantNumeric: "tabular-nums" }}>
                    GSTIN: {shipTo.vat}
                  </div>
                )}
                {entity?.name && shipTo?.name && entity.name !== shipTo.name && (
                  <div data-testid="pdf-customer-entity" style={{ fontSize: typography.fontSize.xs, color: semanticColors.textMuted, marginTop: spacing[4] }}>
                    A unit of {entity.name}
                  </div>
                )}
                {contact?.name && (
                  <div data-testid="pdf-customer-contact" style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary, marginTop: spacing[4] }}>
                    Attn: {contact.name}
                    {contact.email ? ` · ${contact.email}` : ""}
                  </div>
                )}
              </div>
              )}
              <div data-testid="pdf-parties-project">
                <div style={SECTION_LABEL}>Project</div>
                {/* project-id: printed only when stored -- never a "—". */}
                {quote.projectName && (
                  <div data-testid="pdf-project-name" style={{ fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium, color: semanticColors.textPrimary, marginTop: spacing[6] }}>
                    {quote.projectName}
                  </div>
                )}
                {quote.projectId && (
                  <div data-testid="pdf-project-id" style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary, marginTop: spacing[2] }}>
                    Project ID: {quote.projectId}
                  </div>
                )}
                <div style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary, marginTop: spacing[2] }}>
                  End Client: {quote.endClient}
                </div>
              </div>
            </div>

            {/* 3. Validity bar -- only when there is a stored date. */}
            {showValidity && (
            <div
              data-testid="pdf-validity-bar"
              style={{
                backgroundColor: colors.indigo50, border: `1px solid ${colors.indigo100}`,
                borderRadius: radius.base, padding: `${spacing[12]}px ${spacing[16]}px`,
                marginBottom: spacing[24], textAlign: "center",
              }}
            >
              <span style={{ fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.medium, color: colors.indigo800 }}>
                Valid until {formatDate(quote.validUntil)}
              </span>
            </div>
            )}

            {/* 4. Line items table */}
            <table data-testid="pdf-line-items" style={{ width: "100%", borderCollapse: "collapse", marginBottom: spacing[24] }}>
              <thead>
                <tr>
                  {["Sr. No.", "Part No.", "Description", "Spec", "Qty", "UoM", "Unit Price Rs", "Amount Rs"].map((h, i) => (
                    <th key={h} style={{
                      textAlign: i >= 4 ? "right" : "left",
                      fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.semibold, color: semanticColors.textSecondary,
                      textTransform: "uppercase", letterSpacing: typography.letterSpacing.wide,
                      padding: `${spacing[8]}px`, borderBottom: `2px solid ${semanticColors.textPrimary}`, whiteSpace: "nowrap",
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <Fragment key={g.category}>
                    <tr>
                      <td colSpan={8} style={{
                        padding: `${spacing[8]}px`, fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold,
                        color: semanticColors.textSecondary, backgroundColor: semanticColors.panelBg,
                        borderBottom: `1px solid ${semanticColors.border}`,
                      }}>
                        {g.label}
                      </td>
                    </tr>
                    {g.lines.map((l) => (
                      // Fragment: a line can render TWO rows -- itself, and a
                      // note row beneath when it is restricted or carries a
                      // remark.
                      <Fragment key={l.key}>
                      <tr data-testid={`pdf-line-${l.key}`}>
                        <td style={CELL}>{l.srNo}</td>
                        <td style={CELL}>{l.partNo}</td>
                        <td style={CELL}>{l.description}</td>
                        <td style={CELL}>{l.spec}</td>
                        <td style={{ ...CELL, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{l.qty}</td>
                        <td style={{ ...CELL, textAlign: "right" }}>{l.uom}</td>
                        <td style={{ ...CELL, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{formatRupees(l.unitPrice)}</td>
                        <td style={{ ...CELL, textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: typography.fontWeight.medium }}>{formatRupees(l.amount)}</td>
                      </tr>
                      {(l.restricted || l.remark || l.leadTime) && (
                        /* A second row under the line, so the note sits with
                           its line without squeezing a column into the table
                           for something most lines do not carry. */
                        <tr data-testid={`pdf-line-note-${l.key}`}>
                          <td />
                          <td colSpan={7} style={{
                            ...CELL, borderBottom: `1px solid ${semanticColors.border}`,
                            fontSize: typography.fontSize.xs,
                            color: l.restricted ? colors.amber800 : semanticColors.textMuted,
                            fontStyle: l.restricted ? "normal" : "italic",
                          }}>
                            {l.restricted && (
                              <span data-testid={`pdf-line-restricted-${l.key}`}>
                                {/* The reason IS the content of a restricted
                                    line. Printing a bare zero would tell the
                                    customer nothing and read as an error. */}
                                {l.restrictedReason || "Not quoted"}
                              </span>
                            )}
                            {l.restricted && (l.remark || l.leadTime) ? " · " : ""}
                            {l.remark}
                            {l.remark && l.leadTime ? " · " : ""}
                            {l.leadTime ? `Lead time: ${l.leadTime}` : ""}
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    ))}
                    <tr>
                      <td colSpan={6} style={{ borderBottom: `1px solid ${semanticColors.border}` }}></td>
                      <td style={{ ...CELL, textAlign: "right", color: semanticColors.textSecondary }}>Subtotal</td>
                      <td style={{ ...CELL, textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: typography.fontWeight.semibold }}>
                        {formatRupees(g.subtotal)}
                      </td>
                    </tr>
                  </Fragment>
                ))}
              </tbody>
            </table>

            {/* 5. Totals block */}
            <div className="flex justify-end" style={{ marginBottom: spacing[8] }}>
              <div style={{ width: 280 }}>
                <TotalsRow label="Material Total" value={formatRupees(taxableBase)} />
                {taxPending && !tax
                  ? <TotalsRow label="GST" value="…" />
                  : taxLines.map((t) => (
                    <TotalsRow key={t.label} label={t.label} value={formatRupees(t.amount)} />
                  ))}
                <div style={{ borderTop: `1px solid ${semanticColors.border}`, marginTop: spacing[8], paddingTop: spacing[8] }}>
                  <div className="flex items-center justify-between">
                    <span style={{ fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.semibold, color: semanticColors.textPrimary }}>
                      Grand Total
                    </span>
                    <span data-testid="pdf-grand-total" style={{
                      fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.semibold,
                      color: semanticColors.textPrimary, fontVariantNumeric: "tabular-nums",
                    }}>
                      {taxPending && !tax ? "…" : formatRupees(grandTotal)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div style={{ textAlign: "right", fontSize: typography.fontSize.sm, color: semanticColors.textSecondary, fontStyle: "italic", marginBottom: spacing[32] }}>
              {taxPending && !tax ? "" : `Rupees ${numberToWords(grandTotal)} Only`}
            </div>

            {/* 6. Terms */}
            <div style={{ marginBottom: spacing[32] }}>
              <div style={SECTION_LABEL}>Terms &amp; Conditions</div>
              <ul style={{ marginTop: spacing[8], paddingLeft: spacing[16], display: "flex", flexDirection: "column", gap: spacing[4] }}>
                {/* From company_settings, so changing a payment term is an
                    Admin edit rather than a deploy. TERMS is the fallback for
                    the instant before settings arrive, and for a quote opened
                    when they cannot be fetched -- a document with no terms at
                    all is worse than one with the standard set. */}
                {termsLines.map((t, i) => (
                  <li key={i} style={{ fontSize: typography.fontSize.xs, color: semanticColors.textSecondary, lineHeight: 1.5 }}>{t}</li>
                ))}
              </ul>
            </div>

            {/* 7. Footer */}
            <div style={{ borderTop: `1px solid ${semanticColors.border}`, paddingTop: spacing[16], display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
              {/* Every line here comes from company_settings. An unset field
                  renders as nothing rather than a placeholder -- a document
                  is better missing a phone number than carrying a made-up
                  one. */}
              <div
                data-testid="pdf-company-footer"
                style={{ fontSize: typography.fontSize.xs, color: semanticColors.textMuted, lineHeight: 1.6 }}
              >
                {company?.company_name && <>{company.company_name}<br /></>}
                {company?.address && (
                  <>
                    {company.address.split(String.fromCharCode(10)).map((line, i) => (
                      <Fragment key={i}>{line}<br /></Fragment>
                    ))}
                  </>
                )}
                {[
                  // warehouse-master: OUR GSTIN is the DISPATCH WAREHOUSE's (and
                  // its State), from the server's tax block -- frozen with it on
                  // a finalised quote. The company GSTIN is only the last
                  // fallback (no usable warehouse, or a block from before this).
                  ourGstin ? `GSTIN: ${ourGstin}` : "",
                  ourGstin && ourStateName ? `State: ${ourStateName}` : "",
                  company?.contact_email ?? "",
                  company?.contact_phone ?? "",
                ].filter(Boolean).join("  ·  ")}
              </div>
              <div style={{ textAlign: "center", fontSize: typography.fontSize.xs, color: semanticColors.textSecondary }}>
                <div>{company?.company_name ? `For ${company.company_name}` : "For"}</div>
                <div style={{ marginTop: spacing[32], borderTop: `1px solid ${semanticColors.border}`, paddingTop: spacing[4], width: 160 }}>
                  Authorised Signatory
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
